import type { SystemSurfaceId } from "../contracts/system-surface";
import "./system-navigation.css";

export class ShellView {
  readonly #root: HTMLElement;

  constructor(root: HTMLElement) {
    this.#root = root;
    this.#root.classList.add("arcade-root");
  }

  renderBoot(): void {
    this.#renderStatus("Loading", "", "boot");
  }

  renderHome(): HTMLElement {
    this.#root.replaceChildren();

    const shell = document.createElement("section");
    shell.className = "arcade-shell";
    shell.dataset.view = "home";

    const masthead = document.createElement("header");
    masthead.className = "arcade-masthead";

    const logo = document.createElement("h1");
    logo.className = "logo-lockup";
    logo.dataset.carcadeLogo = "true";
    logo.setAttribute("aria-label", "carcade.party");

    const logoImage = document.createElement("img");
    logoImage.className = "logo-lockup__image";
    logoImage.src = "/brand/carcade-logo.png";
    logoImage.alt = "";
    logoImage.decoding = "async";
    logoImage.setAttribute("width", "4096");
    logoImage.setAttribute("height", "2303");
    logoImage.setAttribute("draggable", "false");

    logo.append(logoImage);
    masthead.append(logo);

    const content = document.createElement("main");
    content.className = "arcade-content";
    content.dataset.homeHost = "true";

    const footer = this.#createFooter();

    shell.append(masthead, content, footer, this.#createVersionTrigger());
    this.#root.append(shell);
    return content;
  }

  renderLegal(): void {
    this.#root.replaceChildren();

    const documentPage = document.createElement("main");
    documentPage.className = "legal-document";
    documentPage.setAttribute("aria-label", "Privacy and Terms");

    const home = document.createElement("a");
    home.className = "legal-document__home";
    home.setAttribute("href", "/");
    home.setAttribute("aria-label", "Home");

    const title = document.createElement("h1");
    title.textContent = "Privacy & Terms";

    const privacy = document.createElement("section");
    privacy.id = "privacy";
    const privacyHeading = document.createElement("h2");
    privacyHeading.textContent = "Privacy";
    const privacyBody = document.createElement("p");
    privacyBody.textContent = "When you sign in with Google to create a game, CARCADE receives your Google account identifier and issues you a CARCADE session cookie to keep you signed in; CARCADE does not currently request your email or other Google profile data. CARCADE stores your account, projects, and game versions in Cloudflare D1, and stores generated game files in Cloudflare R2. Creating a game with Create sends your prompt text and the resulting game design to CARCADE's servers for storage, along with which AI model was used and how much -- not the OpenRouter credential itself, which stays in your browser and goes directly to OpenRouter, never to CARCADE. Playing a game from the Rosebud catalog loads that game's cover image and, when you open it, the game itself, directly from Rosebud's and Google's servers, not from CARCADE. CARCADE runs on Cloudflare Workers, which logs basic request metadata (route, method, status, timing) for operating the service, not request contents. To ask about or request deletion of your CARCADE account data, contact support@carcade.party.";
    privacy.append(privacyHeading, privacyBody);

    const terms = document.createElement("section");
    terms.id = "terms";
    const termsHeading = document.createElement("h2");
    termsHeading.textContent = "Terms";
    const termsBody = document.createElement("p");
    termsBody.textContent = "Creating a game with Create requires signing in with Google, and by doing so you confirm you are 18 or older; playing games in the arcade itself does not require an account or sign-in. Keep your Google and OpenRouter accounts secure, use CARCADE lawfully, and do not submit content you do not have the rights to use.";
    terms.append(termsHeading, termsBody);

    documentPage.append(home, title, privacy, terms);
    this.#root.append(documentPage);
  }

  renderLoading(gameId: string): HTMLElement {
    this.#root.replaceChildren();

    const shell = document.createElement("section");
    shell.className = "game-shell";
    shell.dataset.view = "loading-game";

    const host = document.createElement("main");
    host.className = "game-host";
    host.dataset.gameId = gameId;
    host.hidden = true;
    host.setAttribute("aria-hidden", "true");

    const loading = document.createElement("section");
    loading.className = "status-surface game-loading-overlay";
    loading.dataset.loadingOverlay = "true";

    const panel = this.#createStatusPanel("", "");
    const marker = document.createElement("span");
    marker.className = "loading-marker";
    marker.setAttribute("aria-label", "Loading");
    panel.prepend(marker);

    loading.append(panel);
    shell.append(host, loading, this.#createVersionTrigger(true));
    this.#root.append(shell);
    return host;
  }

  #createFooter(): HTMLElement {
    const footer = document.createElement("footer");
    footer.className = "arcade-footer";

    const copyright = document.createElement("span");
    copyright.textContent = "© 2026 ";

    const agency = document.createElement("a");
    agency.setAttribute("href", "https://chamberlain.agency/");
    agency.textContent = "CarCade";

    const separator = document.createElement("span");
    separator.setAttribute("aria-hidden", "true");
    separator.textContent = " · ";

    const legal = document.createElement("a");
    legal.setAttribute("href", "/legal");
    legal.textContent = "Legal";

    footer.append(copyright, agency, separator, legal);
    return footer;
  }

  revealGame(gameId: string, host: HTMLElement): void {
    if (host.dataset.gameId !== gameId) return;
    const shell = host.parentElement;
    if (!shell) return;

    shell.querySelector<HTMLElement>("[data-loading-overlay='true']")?.remove();
    shell.dataset.view = "running-game-shell";
    host.hidden = false;
    host.removeAttribute("aria-hidden");
    host.dataset.view = "running-game";
    host.querySelector<HTMLElement>("[tabindex='0']")?.focus({ preventScroll: true });
  }

  renderGame(gameId: string): HTMLElement {
    this.#root.replaceChildren();

    const shell = document.createElement("section");
    shell.className = "game-shell";
    shell.dataset.view = "running-game-shell";

    const host = document.createElement("main");
    host.className = "game-host";
    host.dataset.view = "running-game";
    host.dataset.gameId = gameId;

    shell.append(host, this.#createVersionTrigger(true));
    this.#root.append(shell);
    return host;
  }

  renderSystemSurfaceLoading(surfaceId: SystemSurfaceId, _title: string): HTMLElement {
    this.#root.replaceChildren();

    const shell = document.createElement("section");
    shell.className = "system-surface-shell";
    shell.dataset.view = "loading-system-surface";
    shell.dataset.systemSurfaceId = surfaceId;

    const host = document.createElement("main");
    host.className = "system-surface-host";
    host.dataset.systemSurfaceId = surfaceId;
    host.hidden = true;
    host.setAttribute("aria-hidden", "true");

    const loading = document.createElement("section");
    loading.className = "status-surface system-surface-loading-overlay";
    loading.dataset.systemSurfaceLoading = "true";

    const panel = this.#createStatusPanel("", "");
    const marker = document.createElement("span");
    marker.className = "loading-marker";
    marker.setAttribute("aria-label", "Loading");
    panel.prepend(marker);

    loading.append(panel);
    shell.append(host, loading, this.#createVersionTrigger(true));
    this.#root.append(shell);
    return host;
  }

  revealSystemSurface(surfaceId: SystemSurfaceId, host: HTMLElement): void {
    if (host.dataset.systemSurfaceId !== surfaceId) return;
    const shell = host.parentElement;
    if (!shell) return;

    shell.querySelector<HTMLElement>("[data-system-surface-loading='true']")?.remove();
    shell.dataset.view = "running-system-surface";
    host.hidden = false;
    host.removeAttribute("aria-hidden");
    host.dataset.view = "system-surface";
  }

  renderRecovery(gameId: string, error: Error, onHome: () => void): void {
    this.#root.replaceChildren();

    const surface = document.createElement("section");
    surface.className = "status-surface";
    surface.dataset.view = "recovering";
    surface.dataset.gameId = gameId;
    surface.dataset.errorName = error.name;

    const panel = this.#createStatusPanel("×", "");
    panel.append(this.#createHomeButton(onHome));
    surface.append(panel);
    this.#root.append(surface);
  }

  renderSystemSurfaceRecovery(surfaceId: SystemSurfaceId, error: Error, onHome: () => void): void {
    this.#root.replaceChildren();

    const surface = document.createElement("section");
    surface.className = "status-surface";
    surface.dataset.view = "recovering-system-surface";
    surface.dataset.systemSurfaceId = surfaceId;
    surface.dataset.errorName = error.name;

    const panel = this.#createStatusPanel("×", "");
    panel.append(this.#createHomeButton(onHome));
    surface.append(panel);
    this.#root.append(surface);
  }

  renderUnsupported(): void {
    this.#renderStatus("×", "Canvas 2D", "unsupported");
  }

  destroy(): void {
    this.#root.replaceChildren();
    this.#root.classList.remove("arcade-root");
  }

  #createHomeButton(onHome: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = "primary-control primary-control--home";
    button.type = "button";
    button.setAttribute("aria-label", "Home");
    // A real glyph, not the two-pseudo-element construction this shared
    // its shape with in Create (also fixed) -- that rendered as a broken
    // chevron-hook, not a house or an arrow. Symbol only here (root rule
    // 12): in-game chrome has no room for a text label.
    button.textContent = "←";
    button.addEventListener("click", onHome, { once: true });
    return button;
  }

  #createVersionTrigger(inGame = false): HTMLSpanElement {
    const version = document.createElement("span");
    version.className = inGame ? "arcade-version arcade-version--game" : "arcade-version";
    version.textContent = "0.1.0";
    version.dataset.version = "true";
    version.title = "Hold for diagnostics";
    version.setAttribute("aria-label", "Version 0.1.0. Hold for diagnostics.");
    return version;
  }

  #createStatusPanel(titleText: string, detailText: string): HTMLElement {
    const panel = document.createElement("div");
    panel.className = "status-panel";

    if (titleText) {
      const title = document.createElement("h1");
      title.textContent = titleText;
      panel.append(title);
    }

    if (detailText) {
      const detail = document.createElement("p");
      detail.textContent = detailText;
      panel.append(detail);
    }

    return panel;
  }

  #renderStatus(titleText: string, detailText: string, view: string): void {
    this.#root.replaceChildren();

    const surface = document.createElement("section");
    surface.className = "status-surface";
    surface.dataset.view = view;
    surface.append(this.#createStatusPanel(titleText, detailText));
    this.#root.append(surface);
  }
}
